import { Injectable } from '@angular/core';
import { PosOfflineService } from './pos-offline.service';
import { PosService } from './pos.service';
import { PosPrintService } from './pos-print.service';
import { fromEvent, firstValueFrom } from 'rxjs';

@Injectable({ providedIn: 'root' })
export class PosSyncService {
  private isSyncing = false;
  private readonly syncIntervalMs = 30_000; // periodic attempt every 30s
  private readonly retryBaseMs = 2000; // base for exponential backoff
  private readonly batchSize = 20;
  private readonly maxAttempts = 5;

  constructor(
    private offline: PosOfflineService,
    private api: PosService,
    private printer: PosPrintService
  ) {
    // 🔹 Initial sync on app start
    this.trySyncPending().catch((e) => console.warn('Initial POS sync failed', e));

    // 🔹 Auto-sync when network comes back online
    fromEvent(window, 'online').subscribe(() => {
      console.log('🌐 Network online — attempting POS sync...');
      this.trySyncPending().catch((e) => console.error('Sync on online failed', e));
    });

    // 🔹 Periodic background sync (every 30s)
    setInterval(() => {
      if (navigator.onLine) this.trySyncPending().catch(() => { });
    }, this.syncIntervalMs);

    // 🔹 Manual sync trigger from any global event
    fromEvent(window, 'run-pos-sync').subscribe(() => this.trySyncPending());
  }

  /** 🔄 Public: Force a sync attempt (can be called from UI or global hook) */
  public async trySyncPending() {
    if (!navigator.onLine) {
      await this.registerBackgroundSync('pos-sync');
      return;
    }

    if (this.isSyncing) return;
    this.isSyncing = true;

    try {
      const pending = await this.offline.getPendingOrders(); // Expect PosOrder[]
      if (!pending?.length) {
        console.log('✅ No pending POS orders to sync');
        return;
      }

      console.log(`🧾 Found ${pending.length} pending POS orders to sync...`);

      for (let i = 0; i < pending.length; i += this.batchSize) {
        const batch = pending.slice(i, i + this.batchSize);
        await this.syncBatchWithRetries(batch);
      }

      console.log('✅ POS sync completed successfully');
      window.dispatchEvent(new CustomEvent('pos-sync-complete'));
    } catch (err) {
      console.error('❌ POS sync failed:', err);
    } finally {
      this.isSyncing = false;
    }
  }

  /** 🔁 Handles one batch of orders with retry + exponential backoff */
  private async syncBatchWithRetries(batch: any[]) {
    let attempt = 0;

    while (attempt < this.maxAttempts) {
      try {
        const resp = await firstValueFrom(this.api.syncOrders(batch)) as any;
        // expected resp.results: [{ localId, serverId }, ...]
          console.log(resp,'server resp')
        if (resp?.length) {
          for (const r of resp) {
            await this.offline.markOrderSynced(r.localId, r.serverId,r.code);

            // Fetch synced order and print confirmed copies
            const localOrder = await this.offline.getOrderByLocalId(r.localId);
            if (localOrder) {
             // await this.printer.printOrderCopies(localOrder, 2);
            }
          }
        }

        // ✅ Success: break retry loop
        return;
      } catch (err) {
        attempt++;
        const backoff = Math.min(this.retryBaseMs * 2 ** attempt, 30_000);
        console.warn(
          `⚠️ Sync attempt ${attempt} failed — retrying in ${backoff}ms...`,
          err
        );
        await this.sleep(backoff);
      }
    }

    console.error('🚫 Max sync attempts reached for batch — will retry later.');
  }

  /** 💤 Utility: Sleep for given ms */
  private sleep(ms: number) {
    return new Promise((res) => setTimeout(res, ms));
  }

  /** 📡 Optional: PWA Background Sync registration (requires SW support) */
  private async registerBackgroundSync(tag: string) {
    if (!('serviceWorker' in navigator) || !('SyncManager' in window)) return;
    try {
      const reg: any = await navigator.serviceWorker.ready;
      if (reg?.sync) {
        await reg.sync.register(tag);
        console.log('🧩 Registered background sync:', tag);
      } else {
        console.warn('⚠️ Background Sync not supported on this browser.');
      }
      console.log('🧩 Registered background sync:', tag);
    } catch (e) {
      console.warn('⚠️ Background Sync registration failed', e);
    }
  }
}
